/**
 * RecordAttachments — F12.D T12.D.3.
 *
 * Załączniki ad-hoc per rekord — drop zone + lista plików w prawym panelu RecordDetailPage.
 * Oddzielnie od pól typu `file`/`image`/`gallery` zdefiniowanych w schemacie modułu.
 *
 * Backend: /api/modules/:moduleId/records/:recordId/attachments (GET list + POST upload).
 *          /api/modules/:moduleId/records/:recordId/attachments/:attachId (GET download URL + DELETE).
 *
 * Limity:
 *  - 50 MB per plik
 *  - 100 załączników per rekord
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Download,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Music,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  description: string | null;
  createdAt: string;
  uploadedByUserId: string | null;
  uploadedByDisplayName: string | null;
  uploadedByEmail: string | null;
}

interface RecordAttachmentsProps {
  moduleId: string;
  moduleSlug: string;
  recordId: string;
}

export function RecordAttachments({ moduleId, moduleSlug, recordId }: RecordAttachmentsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null);

  const listQuery = useQuery({
    queryKey: ['module-record-attachments', moduleSlug, recordId],
    queryFn: async () => {
      const res = await api.get<{ attachments: Attachment[] }>(
        `/modules/${moduleId}/records/${recordId}/attachments`,
      );
      return res.data.attachments;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/modules/${moduleId}/records/${recordId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.attachments.toasts.uploaded') });
      queryClient.invalidateQueries({
        queryKey: ['module-record-attachments', moduleSlug, recordId],
      });
    },
    onError: (err) => {
      const code = isAxiosError(err) ? err.response?.data?.error : null;
      const messageKey =
        code === 'FILE_TOO_LARGE'
          ? 'tenant.attachments.toasts.tooLarge'
          : code === 'ATTACHMENT_LIMIT_EXCEEDED'
            ? 'tenant.attachments.toasts.limitReached'
            : 'tenant.attachments.toasts.uploadError';
      toast.show({ variant: 'error', title: t(messageKey) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (attachId: string) => {
      await api.delete(`/modules/${moduleId}/records/${recordId}/attachments/${attachId}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.attachments.toasts.deleted') });
      queryClient.invalidateQueries({
        queryKey: ['module-record-attachments', moduleSlug, recordId],
      });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.attachments.toasts.deleteError') });
    },
  });

  const handleFileSelected = (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.show({ variant: 'error', title: t('tenant.attachments.toasts.tooLarge') });
      return;
    }
    uploadMutation.mutate(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  const handleDownload = async (att: Attachment) => {
    try {
      // Pobieramy plik jako blob przez nasze API (z autoryzacją).
      // Endpoint strumieniuje plik z magazynu (MinIO) z headerem Content-Disposition.
      const res = await api.get<Blob>(
        `/modules/${moduleId}/records/${recordId}/attachments/${att.id}`,
        { responseType: 'blob' },
      );
      // Tworzymy tymczasowy URL z bloba i triggerujemy pobranie przez ukryty link.
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = att.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Zwalniamy pamięć — blob URL nie jest dłużej potrzebny
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch {
      toast.show({ variant: 'error', title: t('tenant.attachments.toasts.downloadError') });
    }
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSettled: () => setPendingDelete(null),
    });
  };

  const attachments = listQuery.data ?? [];

  return (
    <div className="space-y-3" data-testid="record-attachments">
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition ' +
          (dragOver
            ? 'border-accent-500 bg-accent-500/5'
            : 'border-border-primary hover:border-accent-500/50 hover:bg-bg-tertiary/40')
        }
        data-testid="record-attachments-dropzone"
      >
        <Upload className="mb-2 h-6 w-6 text-text-muted" aria-hidden="true" />
        <p className="text-sm font-medium text-text-secondary">
          {t('tenant.attachments.dropzone.title')}
        </p>
        <p className="mt-1 text-xs text-text-muted">{t('tenant.attachments.dropzone.subtitle')}</p>
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
          className="hidden"
          data-testid="record-attachments-file-input"
        />
        {uploadMutation.isPending && (
          <div className="mt-2 flex items-center gap-2 text-xs text-text-tertiary">
            <Spinner />
            {t('tenant.attachments.uploading')}
          </div>
        )}
      </div>

      {/* Lista załączników */}
      {listQuery.isLoading && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}

      {listQuery.isError && (
        <p className="text-sm text-error-500">{t('tenant.attachments.loadError')}</p>
      )}

      {listQuery.data && attachments.length === 0 && (
        <p
          className="py-4 text-center text-xs text-text-muted"
          data-testid="record-attachments-empty"
        >
          {t('tenant.attachments.empty')}
        </p>
      )}

      {attachments.length > 0 && (
        <ul className="space-y-2" data-testid="record-attachments-list">
          {attachments.map((att) => (
            <AttachmentRow
              key={att.id}
              att={att}
              onDownload={() => handleDownload(att)}
              onDelete={() => setPendingDelete(att)}
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={t('tenant.attachments.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.attachments.confirmDelete.message', { name: pendingDelete.fileName })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ─── Pojedynczy wiersz załącznika ────────────────────────────────────────────

interface AttachmentRowProps {
  att: Attachment;
  onDownload: () => void;
  onDelete: () => void;
}

function AttachmentRow({ att, onDownload, onDelete }: AttachmentRowProps) {
  const { t } = useTranslation();
  const Icon = pickIcon(att.mimeType);

  return (
    <li
      className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm"
      data-testid={`record-attachment-${att.id}`}
    >
      <Icon className="h-5 w-5 shrink-0 text-text-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onDownload}
          className="block w-full truncate text-left font-medium text-text-primary hover:text-accent-600"
          title={att.fileName}
          data-testid={`record-attachment-${att.id}-name`}
        >
          {att.fileName}
        </button>
        <p className="text-xs text-text-tertiary">
          {formatSize(att.sizeBytes)} · {formatDate(att.createdAt)}
          {att.uploadedByDisplayName ? ` · ${att.uploadedByDisplayName}` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDownload();
        }}
        className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
        aria-label={t('tenant.attachments.actions.download')}
        title={t('tenant.attachments.actions.download')}
        data-testid={`record-attachment-${att.id}-download`}
      >
        <Download className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="rounded p-1 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
        aria-label={t('tenant.attachments.actions.delete')}
        title={t('tenant.attachments.actions.delete')}
        data-testid={`record-attachment-${att.id}-delete`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pickIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('audio/')) return Music;
  if (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('text/') ||
    mimeType.includes('document') ||
    mimeType.includes('spreadsheet')
  ) {
    return FileText;
  }
  return FileIcon;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'przed chwilą';
  if (diffMin < 60) return `${diffMin} min temu`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} godz. temu`;
  return d.toLocaleDateString('pl-PL');
}
